/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";

import { ENTRY_DATA_SECTION_KEYS } from "@frontend/common";

import { DealLaScSection } from "@/types/dealLaSc.types";
import { BASIC_DATA_FOR_ADDING_SUB_GROUP_SHELLS } from "@FillInTableContainer/shared/constants/tableCreatorSubGroupShell.constants";
import {
  useTableCreatorFocusStatus,
  DEFAULT_FOCUS_STATUS,
} from "@FillInTableContainer/shared/context/TableCreatorFocusStatusContext";
import { useTableCreatorCurrentHistory } from "@FillInTableContainer/shared/context/TableCreatorHistoryContext";
import {
  useTableCreatorPositionInfo,
  DEFAULT_POSITION_INFO,
} from "@FillInTableContainer/shared/context/TableCreatorPositionInfoContext";
import {
  useValidatedInTableCreator,
  DEFAULT_VALIDATED_INFO,
} from "@FillInTableContainer/shared/context/ValidatedInTableCreatorContext";
import { useUpdateEntryData } from "@hooks/useUpdateArticleData";
import { useEntryDataState } from "@stores/entryData";
import { useInitEntryDataValue } from "@stores/initEntryData";
import { getPressIdFromURL } from "@utils/getStateFromURL";

import type { ArticleEntryData } from "@/types/articleEntryData.types";

interface UseHandleGroupShellReturn {
  clickAddKeyCommentSubGroupShellButton: (
    event: React.MouseEvent<HTMLButtonElement>,
    groupShellInfo: {
      tableIndexParameter: number;
      groupShellIndexParameter: number;
      groupShellKeyParameter: string | null;
      subGroupShellIndexParameter: number;
      subGroupShellKeyParameter: string | null;
    },
  ) => void;
}

interface Props {
  positionPropsInfo: {
    groupTableKey: DealLaScSection;
    groupTableIndex: number;
    groupShellKey: string | null;
    groupShellIndex: number | null;
    subGroupShellIndex: number;
    subGroupShellKey: string | null;
    tableKey: string | null;
    tableIndex: number;
  };
}

export const useAddDealLaScKeyCommentSubGroupShell = ({
  positionPropsInfo,
}: Props): UseHandleGroupShellReturn => {
  const {
    groupTableKey,
    groupTableIndex,
    groupShellKey,
    groupShellIndex,
    subGroupShellIndex,
    subGroupShellKey,
    tableKey,
    tableIndex,
  } = positionPropsInfo;

  const articleSection = ENTRY_DATA_SECTION_KEYS.dealsLaSc;
  const [entryData, setEntryData] = useEntryDataState(getPressIdFromURL());
  const initEntryData = useInitEntryDataValue(getPressIdFromURL());
  const updateEntryData = useUpdateEntryData();
  const { changeCurrentHistory } = useTableCreatorCurrentHistory();
  const { changeFocusStatus } = useTableCreatorFocusStatus();
  const { changeInfo } = useTableCreatorPositionInfo();
  const { changeValidatedInfo } = useValidatedInTableCreator();

  const handleAddKeyCommentListSubGroupShell = (groupShellInfo: {
    subGroupShellIndexParameter: number;
  }) => {
    const { subGroupShellIndexParameter } = groupShellInfo;

    const targetShell = entryData?.[articleSection]?.["Key Comment List"] || [];

    const newArrayLength = targetShell.length + 1;
    const emptyArray = new Array(newArrayLength).fill({});

    const addSubGroupShellData = emptyArray.map((_, index) => {
      const subGroupShellData =
        BASIC_DATA_FOR_ADDING_SUB_GROUP_SHELLS[articleSection][
          "Key Comment List"
        ](initEntryData);

      if (index === subGroupShellIndexParameter) {
        return subGroupShellData;
      }
      if (index > subGroupShellIndexParameter) {
        return targetShell[index - 1];
      }
      return targetShell[index];
    });

    const newFirstItem = (addSubGroupShellData as any)[0];
    const newEntryData: ArticleEntryData = {
      ...entryData,
      [articleSection]: {
        ...entryData[articleSection],
        "Key Comment": (subGroupShellIndexParameter === 0
          ? {
              ...entryData[articleSection]?.["Key Comment"],
              Quote: {
                ...entryData[articleSection]?.["Key Comment"]?.Quote,
                value: newFirstItem?.["Comment Quote"]?.value ?? [],
              },
              Spokesperson: {
                ...entryData[articleSection]?.["Key Comment"]?.Spokesperson,
                value: newFirstItem?.["Comment Spokesperson"]?.value ?? [],
              },
            }
          : entryData[articleSection]?.["Key Comment"]) as any,
        "Key Comment List": addSubGroupShellData as any,
      },
    };

    setEntryData(newEntryData);
    updateEntryData(newEntryData);
    changeCurrentHistory({
      entryData: newEntryData,
      changedDataInfo: {
        target: "subGroupShell",
        action: "add",
        position: {
          groupShellKey,
          groupTableIndex,
          tableKey,
          tableIndex,
          groupTableKey,
          groupShellIndex,
          subGroupShellIndex,
          subGroupShellKey,
          shellKey: null,
          shellIndex: null,
          valueIndex: null,
          value: null,
        },
      },
    });

    changeValidatedInfo(DEFAULT_VALIDATED_INFO);
    changeInfo(DEFAULT_POSITION_INFO);
    changeFocusStatus(DEFAULT_FOCUS_STATUS);
  };

  const handleAddSubGroupShell = (groupShellInfo: {
    subGroupShellIndexParameter: number;
    subGroupShellKeyParameter: string | null;
  }) => {
    const { subGroupShellIndexParameter, subGroupShellKeyParameter } =
      groupShellInfo;

    if (subGroupShellKeyParameter === null) return;

    if (subGroupShellKeyParameter === "Key Comment List") {
      handleAddKeyCommentListSubGroupShell({ subGroupShellIndexParameter });
    }
  };

  const clickAddKeyCommentSubGroupShellButton = (
    event: React.MouseEvent<HTMLButtonElement>,
    groupShellInfo: {
      tableIndexParameter: number;
      groupShellIndexParameter: number;
      groupShellKeyParameter: string | null;
      subGroupShellIndexParameter: number;
      subGroupShellKeyParameter: string | null;
    },
  ) => {
    event.stopPropagation();
    handleAddSubGroupShell(groupShellInfo);
  };

  return {
    clickAddKeyCommentSubGroupShellButton,
  };
};
